/**
 * Feature Flag Provider Component
 *
 * Provides feature flag functionality to the app.
 * When Unleash is configured, uses the real Unleash SDK.
 * When not configured, just renders children (no provider needed).
 *
 * Error Handling:
 * - The Unleash SDK automatically caches flags
 * - If the server is unreachable, it falls back to cached values
 * - If no cache exists, unknown flags remain false until the first response
 * - Errors are logged but don't crash the app
 */

import { type ReactNode, useEffect } from "react";

import * as Sentry from "@sentry/react";
import { FlagProvider, useUnleashClient } from "@unleash/proxy-client-react";

import { UNLEASH_CLIENT, UNLEASH_CONFIG, UNLEASH_CONFIGURED } from "./unleash";

interface FeatureFlagProviderProps {
  children: ReactNode;
}

/**
 * Inner component that subscribes to Unleash events for logging.
 * Must be inside FlagProvider to access the client.
 */
const UnleashEventLogger = ({ children }: { children: ReactNode }) => {
  const client = useUnleashClient();

  useEffect(() => {
    const handleError = (error: Error) => {
      // Log error but don't crash - SDK will use cached/bootstrap values
      console.warn(
        "[FeatureFlags] Unleash error (using cached/default values):",
        error.message,
      );

      Sentry.captureException(error, {
        tags: {
          component: "FeatureFlags",
          unleash: "error",
        },
        level: "warning",
      });
    };

    const handleRecovered = () => {
      // biome-ignore lint/suspicious/noConsole: recovery has no user-visible surface and nothing to report to Sentry, so the console is the only place for this breadcrumb.
      console.info("[FeatureFlags] Unleash connection recovered");
    };

    client.on("error", handleError);
    client.on("recovered", handleRecovered);

    return () => {
      client.off("error", handleError);
      client.off("recovered", handleRecovered);
    };
  }, [client]);

  return <>{children}</>;
};

/**
 * Feature flag provider that wraps the app.
 * Only renders FlagProvider when Unleash is configured.
 */
export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  if (!UNLEASH_CONFIGURED || !UNLEASH_CONFIG) {
    return <>{children}</>;
  }

  return (
    <FlagProvider
      config={UNLEASH_CONFIG}
      unleashClient={UNLEASH_CLIENT ?? undefined}
      startClient={false}
    >
      <UnleashEventLogger>{children}</UnleashEventLogger>
    </FlagProvider>
  );
};
